import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Lock, Share2, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { resolveMediaUrl } from "@/lib/mediaUrl";
import { useApp } from "@/context/AppContext";
import { SharedPostCard } from "@/components/SharedPostCard";

// Lot 614 — « Partager dans un groupe » : choix parmi MES groupes + message facultatif → publication dans le fil
// du groupe (carte de la publication d'origine), compteur de partages incrémenté.
// Lot 747 — plusieurs groupes d'un coup (cases à cocher) : UN appel `group_ids`, un groupe où j'ai déjà partagé est
// sauté par le serveur (« Déjà partagé dans : … »).
export const ShareToGroupSheet = ({ post, open, onOpenChange, onShared }) => {
  const { t } = useApp();
  const nav = useNavigate();
  const [groups, setGroups] = useState(null);
  const [selected, setSelected] = useState([]);
  const [message, setMessage] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setGroups(null); setSelected([]); setMessage(""); setPreview(null);
    api.get("/groups/mine").then(({ data }) => { setGroups(data.groups || []); if (data.groups?.length === 1) setSelected([data.groups[0].id]); }).catch(() => setGroups([]));
    api.get(`/posts/${post.id}/story-preview`).then(({ data }) => setPreview(data.shared_post)).catch(() => {});
  }, [open, post.id]);
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const chosen = (groups || []).filter((g) => selected.includes(g.id));
  const share = async () => {
    if (busy || !chosen.length) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/posts/${post.id}/share-to-group`, { group_ids: chosen.map((g) => g.id), content: message.trim() });
      if (typeof data?.shares_count === "number") onShared?.(data.shares_count);
      onOpenChange(false);
      const done = data?.groups || [];
      if (done.length) {
        const label = done.length === 1
          ? t("post.share_group_done", "Partagé dans « {name} »").replace("{name}", done[0].name)
          : t("post.share_groups_done", "Partagé dans {n} groupes").replace("{n}", String(done.length));
        toast.success(label, { action: { label: t("common.view", "Voir"), onClick: () => nav(`/groups/${done[0].id}`) } });
      }
      if (data?.skipped?.length) toast.info(t("post.share_group_skipped", "Déjà partagé dans : {names}").replace("{names}", data.skipped.map((g) => g.name).join(", ")));
    } catch (e) { toast.error(e?.friendlyMessage || e?.response?.data?.detail || t("common.error", "Erreur")); }
    finally { setBusy(false); }
  };
  const tid = `group-share-${post.id}`;
  const btnLabel = chosen.length > 1
    ? t("post.share_groups_btn", "Partager dans {n} groupes").replace("{n}", String(chosen.length))
    : chosen.length === 1 ? t("post.share_group_btn", "Partager dans « {name} »").replace("{name}", chosen[0].name) : t("post.share_group_title", "Partager dans un groupe");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="bottom" className="z-[130]" overlayClassName="z-[130] bg-black/60" style={{ "--yalola-bottom-sheet-h": "88dvh" }} data-testid={tid}>
        <DialogHeader><DialogTitle>{t("post.share_group_title", "Partager dans un groupe")}</DialogTitle></DialogHeader>
        <DialogBody className="space-y-3">
          {preview && (
            <div className="rounded-2xl border p-2" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface-alt)" }} data-testid={`${tid}-preview`}>
              <SharedPostCard sp={preview} interactive={false} testId={`${tid}-card`} />
            </div>
          )}
          {groups === null && <div className="py-6 grid place-items-center" data-testid={`${tid}-loading`}><Loader2 size={20} className="animate-spin" /></div>}
          {groups && groups.length === 0 && (
            <div className="text-center py-6 space-y-3" data-testid={`${tid}-empty`}>
              <Users size={28} className="mx-auto" style={{ color: "var(--yalola-text-muted)" }} />
              <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }}>{t("post.share_group_empty", "Tu n'es membre d'aucun groupe pour l'instant.")}</p>
              <Button variant="outline" className="rounded-full" onClick={() => { onOpenChange(false); nav("/groups"); }} data-testid={`${tid}-discover`}>{t("post.share_group_discover", "Découvrir les groupes")}</Button>
            </div>
          )}
          {groups && groups.length > 0 && (
            <>
              <div className="flex items-center justify-between text-xs font-semibold" style={{ color: "var(--yalola-text-muted)" }}>
                <span>{t("post.share_group_pick_many", "Choisis un ou plusieurs groupes")}</span>
                {chosen.length > 0 && <span data-testid={`${tid}-count`}>{chosen.length}</span>}
              </div>
              <div className="space-y-1.5 max-h-[34dvh] overflow-y-auto pr-0.5" data-testid={`${tid}-list`}>
                {groups.map((g) => { const on = selected.includes(g.id); return (
                  <button key={g.id} type="button" onClick={() => toggle(g.id)} aria-pressed={on} role="checkbox" aria-checked={on}
                          className="w-full flex items-center gap-3 rounded-2xl border p-2.5 text-left transition-colors"
                          style={{ borderColor: on ? "var(--yalola-primary)" : "var(--yalola-border)", background: on ? "var(--yalola-primary-soft)" : "transparent" }}
                          data-testid={`${tid}-group-${g.id}`}>
                    <span className="h-10 w-10 rounded-xl overflow-hidden shrink-0 grid place-items-center" style={{ background: "var(--yalola-surface-alt)" }}>
                      {g.cover_url ? <img src={resolveMediaUrl(g.cover_url)} alt="" className="h-full w-full object-cover" /> : <Users size={18} style={{ color: "var(--yalola-text-muted)" }} />}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-semibold truncate">{g.name}</span>
                      <span className="block text-xs flex items-center gap-1" style={{ color: "var(--yalola-text-muted)" }}>
                        {g.privacy === "private" && <Lock size={11} />}{t("group.members_n", "{n} membre(s)").replace("{n}", String(g.member_count || 0))}
                      </span>
                    </span>
                    <span className="h-5 w-5 rounded-md border-2 grid place-items-center shrink-0" style={{ borderColor: on ? "var(--yalola-primary)" : "var(--yalola-border)", background: on ? "var(--yalola-primary)" : "transparent" }}>
                      {on && <Check size={13} color="#fff" strokeWidth={3} />}
                    </span>
                  </button>
                ); })}
              </div>
              <Textarea value={message} onChange={(e) => setMessage(e.target.value.slice(0, 500))} rows={2} placeholder={t("post.share_group_message", "Ajoute un message (facultatif)")} className="rounded-xl text-sm" data-testid={`${tid}-message`} />
            </>
          )}
        </DialogBody>
        {groups && groups.length > 0 && (
          <DialogFooter>
            <Button onClick={share} disabled={busy || !chosen.length} className="w-full sm:w-auto rounded-full" data-testid={`${tid}-submit`}>
              {busy ? <Loader2 size={16} className="mr-1.5 animate-spin" /> : <Share2 size={16} className="mr-1.5" />}
              {btnLabel}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ShareToGroupSheet;
